import { AlternativeInformation } from "@components/alternatives/AlternativeInformation";
import { AlternativesCards } from "@components/alternatives/AlternativesCards";
import { AlternativesCTASection } from "@components/alternatives/AlternativesCTASection";
import { AlternativesTopBannerCTASection } from "@components/alternatives/AlternativesTopBannerCTASection";
import { ContentParagraph } from "@components/alternatives/ContentParagraph";
import { Herosection, HeroSectionProps } from "@components/alternatives/HeroSection";
import { OverviewContent, OverviewGrid } from "@components/alternatives/OverviewGrid";
import RatingsAndReviews from "@components/alternatives/RatingsAndReviews";
import AwardsSection from "@components/new-ui/AwardsSection";
import CTASection from "@components/new-ui/CTASection";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import NewLayout from "@components/new-ui/Layout";
import { Navbar } from "@pages/case-study/alrannan-administrative-services";
import React from "react";
import { aisensyNavLinks, alternativesCardsDetails } from "../../components/alternatives/constant";
import { AlternativesInfo } from "./interakt";
import { extractTextFromJSX } from "@utils/schemaGenerator";

const herosectionData: HeroSectionProps = {
    chipTitle: "Alternatives",
    title: "Gallabox vs AiSensy — Which WhatsApp Platform Wins?",
    primaryButtonText: "Try for Free",
    imageUrl: "https://assets.gallabox.com/gb-home/alternatives/aisensy/aisensy-hero.png",
    imgAlt: "alternatives-interakt",
};

const whatsappGrowth: AlternativesInfo[] = [
    {
        id: "1",
        title: "1. Gallabox",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">
                    <span className="text-lg font-bold">USP over AiSensy:</span> Robust, meaningful automation, and effective lead pipelines.
                </p>
                <p className="text-gray-900 text-lg pt-2">
                    Gallabox is an end-to-end WhatsApp Business suite that allows you to capture, nurture, and convert leads. All its features focus on robust automation and
                    powering your operations with reliable, accurate AI.
                </p>
            </div>
        ),
        image: "https://assets.gallabox.com/gb-home/alternatives/aisensy/gallabox-automation.png",
        imgAlt: "gallabox-automation",

        features: {
            title: "Features:",
            featuresList: [
                {
                    item: (
                        <p className="py-2 text-lg">
                            1.{" "}
                            <a href="https://gallabox.com/whatsapp-flows" className="underline pr-2">
                                WhatsApp Flows:{" "}
                            </a>
                            Create forms in minutes, integrate them in chatbots, broadcasts, etc. manage data efficiently with tables, and more.
                        </p>
                    ),
                },
                {
                    item: <p className="py-2 text-lg">2. Gallabox GenAI: Personalize responses, AI rewrite for agents, AI-powered chatbots, and more.</p>,
                },
                {
                    item: (
                        <p className="py-2 text-lg">
                            3. WhatsApp Business API: Custom automation, multi-device operation, unlimited contact syncing, hassle-free integrations, and more.
                        </p>
                    ),
                },
                {
                    item: (
                        <p className="py-2 text-lg">
                            4.{" "}
                            <a href="https://gallabox.com/blog/whatsapp-team-inbox" className="underline pr-2">
                                Shared Inbox:{" "}
                            </a>
                            Make notes, tag team members, assign conversations, view chat histories, and more.
                        </p>
                    ),
                },

                {
                    item: (
                        <p className="py-2 text-lg">
                            5.{" "}
                            <a href="https://gallabox.com/whatsapp-chatbot" className="underline pr-2">
                                WhatsApp Chatbot:{" "}
                            </a>
                            Connect with ChatGPT, no-code builder, conversation workflows, contact assignment, and more.
                        </p>
                    ),
                },
                {
                    item: <p className="py-2 text-lg">6. Drip Marketing: Customer onboarding, event reminders, product launches, follow-ups, and more.</p>,
                },
                {
                    item: (
                        <p className="py-2 text-lg">
                            7. WhatsApp Broadcast: Contact segmentation, reply-to configurations, campaign performance, abandoned cart triggers, and more.
                        </p>
                    ),
                },
                {
                    item: (
                        <p className="py-2 text-lg">
                            8.{" "}
                            <a href="https://gallabox.com/click-to-whatsapp-ads" className="underline  pr-2">
                                Click-to-WhatsApp Ads:
                            </a>{" "}
                            Instant responses, feedback collection, reselling, upselling and retention tools, integrations, and more.
                        </p>
                    ),
                },

                {
                    item: <p className="py-2 text-lg">9. WhatsApp Payments: One-click payment links, payment workflows, invoice collection, service bookings, and more.</p>,
                },
                {
                    item: <p className="py-2 text-lg">10. WhatsApp Shop: Product creation and management, categorization and bundling, chatbots and flows, and more.</p>,
                },
            ],
        },
    },
];

const delightChat: AlternativesInfo[] = [
    {
        id: "1",
        title: "2. DelightChat",
        image: "https://assets.gallabox.com/gb-home/alternatives/aisensy/delightChat.png",
        imgAlt: "delightChat",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">
                    <span className="text-lg font-bold">USP over AiSensy:</span> Powerful support features and WhatsApp marketing and messaging solutions
                </p>
                <p className="text-gray-900 text-lg pt-2">
                    DelightChat helps your business sell products and provide customer support easily on WhatsApp. You get an omnichannel customer support inbox and a full WhatsApp
                    marketing suite to streamline operations.
                </p>
            </div>
        ),
        features: {
            title: "Features:",
            featuresList: [
                {
                    item: "WhatsApp API: Use a single WhatsApp API number to engage and sell to customers.",
                },
                {
                    item: "Order Confirmation: Send order confirmations and upsell coupon codes to customers.x",
                },
                {
                    item: "COD verification: Reduce RTOs by confirming COD orders on WhatsApp.",
                },
                {
                    item: "Built-in payment processing for secure transactions within WhatsApp.",
                },
                {
                    item: (
                        <li className="py-2 text-lg">
                            WhatsApp Broadcasts: Create personalized templates and generate{" "}
                            <a href="https://www.delightchat.io/feature#:~:text=templates%20and%20generate-,26x%20ROI%20on,-marketing%20spends.%20Up" className="underline pr-2">
                                26x
                            </a>{" "}
                            ROI on marketing spending.
                        </li>
                    ),
                },
            ],
        },
    },
];

const watiChat: AlternativesInfo[] = [
    {
        id: "1",
        title: "3. WaliChat",
        image: "https://assets.gallabox.com/gb-home/alternatives/aisensy/watiChat.png",
        imgAlt: "watiChat",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">
                    <span className="text-lg font-bold">USP over AiSensy:</span> Offers comprehensive collaboration tools and a multi-agent platform
                </p>
                <p className="text-gray-900 text-lg pt-2">WaliChat helps you quickly mobilize marketing campaigns on WhatsApp and scale your WhatsApp communications.</p>
            </div>
        ),
        features: {
            title: "Features:",
            featuresList: [
                {
                    item: "Multiagent Chat: Distribute chats amongst agents to balance workloads, use multiple numbers simultaneously and remotely.",
                },
                {
                    item: "Analytics: Monitor team activity, spot improvement opportunities, obtain structured data, measure customer engagement and more.",
                },
                {
                    item: "Productivity Tools: Internal notes, assign labels and metadata to conversations, send quick replies, and more.",
                },
                {
                    item: "WhatsApp API and Webhooks: Workflow automation, build chatbots, send automated messages, and more.",
                },
                {
                    item: "Multiple WhatsApp Numbers: Switch from one number to another in a click, and manage multiple numbers easily.",
                },
            ],
        },
    },
];

const botPenguin: AlternativesInfo[] = [
    {
        id: "1",
        title: "4. BotPenguin",
        image: "https://assets.gallabox.com/gb-home/alternatives/aisensy/botPenguin.png",
        imgAlt: "botPenguin",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">
                    <span className="text-lg font-bold">USP over AiSensy:</span> Performant chatbots and WhatsApp marketing features that are easy to set up
                </p>
                <p className="text-gray-900 text-lg pt-2">
                    BotPenguin is a chatbot-focused marketing solution that allows you to design your chatbots for various platforms. It powers WhatsApp with the official WhatsApp
                    Business API with enhanced automation features.
                </p>
            </div>
        ),
        features: {
            title: "Features:",
            featuresList: [
                {
                    item: "AI Chatbot: WhatsApp chatbots for selling, support, appointments, and more functions.",
                },
                {
                    item: "WhatsApp Automation: Embeddable widgets, message campaigns, alerts and push, customer retargeting, customer segmentation, and more.",
                },
                {
                    item: "Other features: Lead generation, customer support, FB automation, analytics, unified inbox, live chat, integrations, and marketing automation.",
                },
            ],
        },
    },
];

const twilio: AlternativesInfo[] = [
    {
        id: "1",
        title: "5. Twilio",
        image: "https://assets.gallabox.com/gb-home/alternatives/aisensy/twilio.png",
        imgAlt: "twilio",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">
                    <span className="text-lg font-bold">USP over AiSensy:</span> Unparalleled flexibility, high scalability, and robust APIs
                </p>
                <p className="text-gray-900 text-lg pt-2">
                    Twilio is a holistic API and messaging platform that allows you to engage WhatsApp customers with data-driven insights. It also provides detailed documentation
                    to help you start quickly.
                </p>
            </div>
        ),
        features: {
            title: "Features:",
            featuresList: [
                {
                    item: "Transactional messaging: Send one-way messages like notifications, alerts, promotions, and marketing content.",
                },
                {
                    item: "Conversational messaging: Leverage a single API for customer care and conversational commerce.",
                },
                {
                    item: "Contact center: Use various channels like email, voice, SMS, etc., to send personalized communications.",
                },
                {
                    item: "Personalized communications: Gather insights to power personalization across various channels, including WhatsApp.",
                },
                {
                    item: "Account verification: Build customer trust by verifying customers over WhatsApp.",
                },
            ],
        },
    },
];

const tidio: AlternativesInfo[] = [
    {
        id: "1",
        title: "6. Tidio",
        image: "https://assets.gallabox.com/gb-home/alternatives/aisensy/tidio.png",
        imgAlt: "tidio",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">
                    <span className="text-lg font-bold">USP over AiSensy:</span> Extends full-suite customer service platform with chatbot and automation
                </p>
                <p className="text-gray-900 text-lg pt-2">
                    Tidio is primarily a customer service platform that also facilitates chatbots, automation, and AI agents and integrations to help you manage WhatsApp marketing.
                </p>
            </div>
        ),
        features: {
            title: "Features:",
            featuresList: [
                {
                    item: "AI Agent: Simple and configurable setup, see conversations in real-time, jump in and monitor agent conversations, and more.",
                },
                {
                    item: "Live chat: Shared inbox, visitor lists, analytics, and more.",
                },
                {
                    item: "Help desk: Ticketing, multichannel modes, live chat, and more.",
                },
                {
                    item: "Flows: ROI calculator, sales tactics for growth, replicate in-store shopping.",
                },
                {
                    item: "Integrations: Shopify, WordPress, and more.",
                },
            ],
        },
    },
];

const chatBot: AlternativesInfo[] = [
    {
        id: "1",
        title: "7. ChatBot",
        image: "https://assets.gallabox.com/gb-home/alternatives/aisensy/chatBot.png",
        imgAlt: "chatBot",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">
                    <span className="text-lg font-bold">USP over AiSensy:</span> Provides advanced multilingual support and AI-native chatbots for WhatsApp
                </p>
                <p className="text-gray-900 text-lg pt-2">
                    ChatBot is, as the name suggests, a platform to let you create custom chatbots for any purpose. Leverage the power of AI to power WhatsApp conversations with
                    data-backed insight.
                </p>
            </div>
        ),
        features: {
            title: "Features:",
            featuresList: [
                {
                    item: "AI chatbot: AI assist, AI knowledge, small talk, and more.",
                },
                {
                    item: "Live chat automation: Transfer to agent, goals, tags, and more.",
                },
                {
                    item: "Chatbot implementation: Chat widget for websites, widget customization, Slack assistant, and more.",
                },
                {
                    item: "Visual builder: Testing tools, flows, version control, and more.",
                },
                {
                    item: "Rich messages: Greetings, welcome messages, quick replies, images, buttons, and more.",
                },
            ],
        },
    },
];

const wati: AlternativesInfo[] = [
    {
        id: "1",
        title: "8. Wati",
        image: "https://assets.gallabox.com/gb-home/alternatives/aisensy/wati.png",
        imgAlt: "wati",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">
                    <span className="text-lg font-bold">USP over AiSensy:</span> Helps simplifying your WhatsApp communications and grow your SMB over WhatsApp
                </p>
                <p className="text-gray-900 text-lg pt-2">
                    Wati is a WhatsApp-focused business solution ideal for small and mid-sized companies that are in the phase of rapid growth.
                </p>
            </div>
        ),
        features: {
            title: "Features:",
            featuresList: [
                {
                    item: "Click-to-WhatsApp ads: Prompt responses, special promotions, customer retargeting, guided messaging, and more.",
                },
                {
                    item: "Shared inbox: Organize teams, manage contacts, see all messages and threads, tag and identify categories like VIPs, etc., and more.",
                },
                {
                    item: (
                        <li className="py-2 text-lg">
                            Broadcast and bulk messages: Campaign management,{" "}
                            <a href="https://gallabox.com/blog/whatsapp-schedule-message" className="underline pr-2">
                                message scheduling
                            </a>
                            ,multilingual conversations, message templates, and more.
                        </li>
                    ),
                },
                {
                    item: "Custom notifications: Alert customers about stock, cross-sell products, special offer notifications, and more.",
                },
                {
                    item: "No-code chatbot: Connect multiple chatbots, automate up to 200 steps, and more.",
                },
            ],
        },
    },
];

const gupshup: AlternativesInfo[] = [
    {
        id: "1",
        title: "9. Gupshup",
        image: "https://assets.gallabox.com/gb-home/alternatives/aisensy/gupShup.png",
        imgAlt: "gupShup",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">
                    <span className="text-lg font-bold">USP over AiSensy:</span> Better analytics that is cost-effective to obtain
                </p>
                <p className="text-gray-900 text-lg pt-2">
                    Gupshup is a full-fledged conversational AI agent platform that can help you get more leads and drive conversions on your WhatsApp channel. Its AI modules
                    provide excellent contextuality to chatbot conversations.
                </p>
            </div>
        ),
        features: {
            title: "Features:",
            featuresList: [
                {
                    item: "Conversational marketing: Click-to-chat ads, one-click broadcast campaigns, visual journey builder, campaign performance, etc.",
                },
                {
                    item: "Conversational commerce: Drip campaigns, offer and alert automation, 1:1 buying assistance, purchase assistance, and more.",
                },
                {
                    item: "Conversational support: Automate common query responses, unified inbox for collaboration, self-serve, and relevant recommendations for ease of service.",
                },
            ],
        },
    },
];

const infobip: AlternativesInfo[] = [
    {
        id: "1",
        title: "10.  Infobip",
        image: "https://assets.gallabox.com/gb-home/alternatives/aisensy/infobip.png",
        imgAlt: "infobip",
        description: (
            <div className="text-gray-900 text-lg">
                <p className="text-gray-900 text-lg pb-6">
                    <span className="text-lg font-bold">USP over AiSensy:</span> Leverage better APIs and deliver memorable conversational experiences with Gen AI
                </p>
                <p className="text-gray-900 text-lg pt-2">
                    Infobip is an AI platform that allows you to build or access multiple solutions like conversational chatbots, customer engagement solutions, customer data
                    platforms, GenAI platforms, and more. It can power your WhatsApp conversations with AI chatbots and generative capabilities.
                </p>
            </div>
        ),
        features: {
            title: "Features:",
            featuresList: [
                {
                    item: "Awareness: Two-way conversations, push notifications, product recommendations, end-to-end customer journeys, and more.",
                },
                {
                    item: "Consideration: Share product catalogs, GenAI prompts, quick purchase experiences, and more.",
                },
                {
                    item: "Purchase: In-app payments, trigger cart abandonment messages, and more.",
                },
                {
                    item: "Support: Share loyalty programs, updates, and offers after purchases easily, and more.",
                },
                {
                    item: "Retain: Leverage customer 360-degree views to share personalized recommendations.",
                },
            ],
        },
    },
];

const content: OverviewContent = {
    title: ["Software", "Best suited for", "USP", "Why it’s a good alternative to AiSensy"],
    rows: [
        [
            ["Gallabox"],
            ["Lead qualification", "Customer conversation automation"],
            ["AI-powered, no-code chatbots that provide seamless lead qualification and nurturing"],
            ["Provides advanced automation features for lead management that reduce manual intervention."],
        ],
        [
            ["DelightChat"],
            ["Omnichannel marketing and support for eCommerce businesses"],
            ["Unified dashboard to manage communications across multiple channels"],
            ["Combines marketing and support in one tool"],
        ],
        [
            ["WaliChat"],
            ["Simultaneous management of multiple WhatsApp numbers and campaigns"],
            ["CRM-enabled features with analytics and a multiagent platform, ideal for collaboration"],
            ["Improves campaign management with robust collaboration tools"],
        ],

        [
            ["BotPenguin"],
            ["Creating no-code chatbots for marketing and support"],
            ["80+ integrations, AI-powered chatbots, and a free forever plan"],
            ["Has one of the most cost-effective chatbots"],
        ],
        [
            ["Twilio"],
            ["Scalability and data-driven customer engagement"],
            ["Personalized messaging for multiple channels with comprehensive API."],
            ["Has unmatched scalability and a flexible API"],
        ],
        [
            ["Tidio"],
            ["Lead generation", "Customer support automation"],
            ["Seamless communications through AI-powered, multichannel chatbots."],
            ["Facilitates AI-driven automation and multichannel integration"],
        ],
        [
            ["ChatBot"],
            ["Creation of customizable AI chatbots"],
            ["Personalized customer interactions with multilingual support."],
            ["Provides expert multilingual support with advanced AI"],
        ],
        [
            ["Wati"],
            ["Streamlining sales, marketing, and customer support for SMBs"],
            ["No-code chatbots and shared team inboxes provide end-to-end support."],
            ["Designed for SMBs that wish to simplify communications"],
        ],
        [
            ["Gupshup"],
            ["Enterprises looking for self-service in WhatsApp marketing and support"],
            ["A pay-as-you-use model with zero setup cost"],
            ["Offers detailed analytics in a cost-effective way"],
        ],
        [
            ["Infobip"],
            ["Medium to large businesses that need real-time customer communication support"],
            ["Rich media messaging, end-to-end encryption, and chatbot integration"],
            ["Provides advanced API features with versatile communication options"],
        ],
    ],
};

const defaultFAQData: FAQSectionProps = {
    title: "Got questions? Perhaps these FAQs can help!",
    faqItems: [
        {
            question: "1.Are there any free or open-source platforms similar to AiSensy?",
            answer: "Yes, platforms like Whatso, Whatsender, and WhatsHash are open-source platforms similar to AiSensy. However, if you want a comprehensive WhatsApp marketing solution, you can select one from the list in this blog.",
        },
        {
            question: "2.What are the limitations of AiSensy that users have encountered?",
            answer: "Users have encountered problems like absence of mobile app, automation limitations, limited integration for Shopify, delayed template approvals, and no support for multi-product catalogs with AiSensy.",
        },
    ],
};

const allAlternatives: AlternativesInfo[] = [...whatsappGrowth, ...delightChat, ...watiChat, ...botPenguin, ...twilio, ...tidio, ...chatBot, ...wati, ...gupshup, ...infobip];

const simplifiedAlternatives: { title: string; description: string; imageUrl: string }[] = allAlternatives.map(({ title, description, image }) => ({
    title,
    description: extractTextFromJSX(description),
    imageUrl: image as string,
}));

const Aisensy: React.FC = () => {
    return (
        <NewLayout
            title="Gallabox vs AiSensy — Full Comparison for WhatsApp Business API"
            description="See how Gallabox compares to AiSensy on chatbots, broadcast, API pricing & support. Detailed feature-by-feature breakdown inside."
            canonical="https://gallabox.com/alternatives/aisensy"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/alternatives/aisensy/aisensy-alternative-og.png" }}
            schema={{
                heroOrOgImageAlt: "alternative",
                heroOrOgImageDescription: "11 Best AiSensy Alternatives to scale your revenue on WhatsApp",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/alternatives/aisensy/aisensy-hero.png",
                faq: defaultFAQData.faqItems,
                alternativesInfo: simplifiedAlternatives,
            }}
        >
            <Herosection {...herosectionData} />

            <section className="max-w-6xl md:mx-36 mx-auto md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <p className="text-gray-900 text-xl font-semibold pb-5">Table of contents</p>
                        <Navbar links={aisensyNavLinks} />
                    </div>
                </div>

                <div className="col-span-4 md:pl-4 px-4">
                    <ContentParagraph
                        content={
                            <p className="text-lg pb-6 text-left">
                                WhatsApp Business has become a baseline marketing channel for countless businesses around the world. The reasons are clear – the channel offers a
                                whopping
                                <a href="https://business.whatsapp.com/blog/use-whatsapp-business-goals" className="underline text-blue-500 p-2">
                                    98% open rate
                                </a>
                                with users spending about
                                <a href="https://www.statista.com/statistics/1294880/time-spent-whatsapp-app-selected-countries/" className="underline text-blue-500 p-2">
                                    16 hours a month
                                </a>
                                on the platform.
                            </p>
                        }
                    />
                    <ContentParagraph
                        content={[
                            "If you intend to reach users where they like to spend their time most, then WhatsApp is high on the list of such channels. Using the WhatsApp Business account, you can set up the basic workings of an outreach campaign to reach your target audience. You can augment these operations with WhatsApp-oriented tools like AiSensy.",
                            "However, while AiSensy offers robust marketing capabilities for WhatsApp, your business may require certain unique capabilities, like omnichannel dashboards, which it does not offer.",
                            "This blog curates 10 unique WhatsApp Business tools you can use as AiSensy alternatives and find the features specific to your business needs.",
                        ]}
                    />
                    <h2 className="text-black text-5xl font-semibold pb-8 mt-10">But first: Is it time to upgrade from WhatsApp Business to WhatsApp Business API?</h2>
                    <ContentParagraph
                        content={[
                            "A WhatsApp Business account provides many capabilities to a business, but there are several things where it falls short of delivering a great customer experience.",
                            "Here are ten key indicators that emphasize that it is time for your business to reinforce your WhatsApp Business account with robust marketing tools:",
                        ]}
                    />
                    <ContentParagraph
                        title="1. Increasing Message Volume"
                        content={[
                            "If your business finds it challenging to respond timely to the accumulating message loads, it is a sign that the basic WhatsApp Business account cannot serve your needs. This indicates that it's time to set up automation to handle the high volume of incoming messages.",
                        ]}
                    />
                    <ContentParagraph
                        title="2. Lacking Support Power"
                        content={[
                            "For growing businesses, it can be difficult to delegate human agents to respond to WhatsApp queries all the time. If your agents are overwhelmed responding to WhatsApp queries, it demands an upgrade and installation of a chatbot to handle basic-level requests automatically.",
                        ]}
                    />
                    <ContentParagraph
                        title="3. Compliance Challenges"
                        content={[
                            "Increasing WhatsApp interactions also translates into increasing customer data, which requires companies to process it according to prevailing regulations. On a large scale, it may not be sustainable to rely on basic WhatsApp Business apps to keep you compliant.",
                        ]}
                    />
                    <ContentParagraph
                        title="4. Insufficient Reporting"
                        content={[
                            "The basic WhatsApp Business tool is sufficient for businesses that aren’t tracking metrics like response times or customer engagement. The moment your business requires detailed insight into the performance of your marketing campaigns, it is time to switch.",
                        ]}
                    />
                    <ContentParagraph
                        title="5. Mounting Repetitive Tasks"
                        content={[
                            "It is not sustainable to engage your human workforce in tasks like addressing basic customer queries, tracking appointments and follow-ups, and other clerical tasks.",
                            "With growing messaging volume, these tasks also increase, bogging your agents down with avoidable workload. This is a clear indication that a switch is required.",
                        ]}
                    />
                    <ContentParagraph
                        title="6. Inconsistent Branding"
                        content={[
                            "As a brand, you have a specific tone and vocabulary for customer interactions. However, different human agents can introduce variations in branded messaging, working to confuse your audience.",
                            "If this has begun to happen with your business, it is time to consider an extensive WhatsApp Business app.",
                        ]}
                    />
                    <ContentParagraph
                        title="7. Increasing Response Times"
                        content={[
                            "With WhatsApp channels, your audience expects 24x7 support – but when they don’t get it, it causes churn. If your response times are steadily increasing, it means that your current tools fail to handle incoming messages efficiently.",
                        ]}
                    />
                    <ContentParagraph
                        title="8. Manual Data Entry"
                        content={[
                            "The increasing volume of messages also means that your team has to transfer all this data into the CRM manually, which takes up more time as your customer base widens. As you scale up, it is a smart move to automate this process with shiny new tools.",
                        ]}
                    />
                    <ContentParagraph
                        title="9. Single-Language Limitation"
                        content={[
                            "If your business is planning to enter new geographical markets, adding new languages to your support systems is sensible. If your current WhatsApp Business apps do not offer multilingual support, it is time to upgrade.",
                        ]}
                    />
                    <ContentParagraph
                        title="10. Lack of Personalization at Scale"
                        content={[
                            "It is easy to provide personalized experiences to a limited audience early on. However, with a continuously growing customer base, if your business struggles to make customers feel understood, it may indicate you need a better WhatsApp Business solution.",
                        ]}
                    />
                    <h2 className="text-3xl font-semibold pb-5"> Top 10 AiSensy Alternatives for WhatsApp Business in 2025</h2>
                    <ContentParagraph
                        content={[" Curated below is a comprehensive list of AiSensy alternatives that each provide a unique offering as compared to AiSensy and each other."]}
                    />

                    <OverviewGrid data={content} />

                    <div className="flex flex-col mt-10">
                        <section id="Gallabox">
                            <AlternativeInformation data={whatsappGrowth} />
                            <RatingsAndReviews ratings={["G2: 4.6/5 (150+ reviews)", "Capterra: 4.5/5 (N/A)"]} />
                        </section>

                        <section id="DelightChat" className="pt-10">
                            <AlternativeInformation data={delightChat} />
                            <RatingsAndReviews ratings={["G2: 4.5/5 (N/A)", "Capterra: N/A"]} />
                        </section>

                        <section id="WaliChat" className="pt-10">
                            <AlternativeInformation data={watiChat} />
                            <RatingsAndReviews ratings={["G2: N/A", "Capterra: N/A"]} />
                        </section>

                        <section id="BotPenguin" className="pt-10">
                            <AlternativeInformation data={botPenguin} />
                            <RatingsAndReviews ratings={["G2: 4.5/5 (N/A)", "Capterra: 5/5 (N/A)"]} />
                        </section>

                        <section id="Twilio" className="pt-10">
                            <AlternativeInformation data={twilio} />
                            <RatingsAndReviews ratings={["G2: 4.2/5 (470+ reviews)", "Capterra: 4.4/5 (470+ reviews)"]} />
                        </section>

                        <section id="Tidio" className="pt-10">
                            <AlternativeInformation data={tidio} />
                            <RatingsAndReviews ratings={["G2: 4.7/5 (1,610+ reviews)", "Capterra: 4.7/5 (460+ reviews)"]} />
                        </section>

                        <section id="ChatBot" className="pt-10">
                            <AlternativeInformation data={chatBot} />
                            <RatingsAndReviews ratings={["G2: 4.3/5 (N/A)", "Capterra: 4.6/5 (50+ reviews)"]} />
                        </section>

                        <section id="Wati" className="pt-10">
                            <AlternativeInformation data={wati} />
                            <RatingsAndReviews ratings={["G2: 4.6/5 (300+ reviews)", "Capterra: 4.6/5 (170+ reviews)"]} />
                        </section>

                        <section id="Gupshup" className="pt-10">
                            <AlternativeInformation data={gupshup} />
                            <RatingsAndReviews ratings={["G2: 4.4/5 (50+ reviews)", "Capterra: 4.3/5 (N/A)"]} />
                        </section>

                        <section id="Infobip" className="pt-10">
                            <AlternativeInformation data={infobip} />
                            <RatingsAndReviews ratings={["G2: 4.3/5 (54 reviews)", "Capterra: 4.6/5 (14 reviews)"]} />
                        </section>
                    </div>
                </div>

                <AlternativesTopBannerCTASection />
            </section>

            <div className="max-w-6xl mx-auto py-20 px-2">
                <h2 className="text-black text-4xl font-semibold pb-5">Things to Consider Before Switching to AiSensy Alternatives</h2>
                <ContentParagraph
                    content={["If you have been using AiSensy to augment WhatsApp operations, there will be certain challenges to navigate to make the switch a success:"]}
                />
                <h6 className="text-black text-xl font-semibold pb-5">Paying Attention to Technology</h6>
                <div className="text-lg pb-6">
                    Before you can make the switch to a more detailed platform, you would need to go through several other steps first:
                    <ul>
                        <li>Setting up an account on Facebook Business Manager</li>
                        <li>Verifying your business on the interface</li>
                        <li>Configuring the API to integrate the new solution</li>
                        <li>Hiring IT professionals or experts to execute the switch or creating an internal team for the purpose</li>
                        <li>Seeking third-party assistance if further integrations are required, like CRMs, payment systems, etc.</li>
                    </ul>
                </div>
                <h6 className="text-black text-xl font-semibold pb-5">Assessing Costs</h6>
                <ContentParagraph
                    content={[
                        "It is important to understand that all business APIs have different cost implications. Some have a one-time purchase cost, others have a subscription, while many offer pay-as-you-go services.",
                        "Then, there are maintenance, troubleshooting, and upgrading costs to consider. Make sure that the budget you prepare accounts for all these expenses.",
                    ]}
                />

                <h6 className="text-black text-xl font-semibold pb-5">Managing Approvals</h6>
                <ContentParagraph
                    content={[
                        "Businesses get the WhatsApp Business API through an approval process that includes setting up a Facebook Business Manager account and verifying it.",
                        "Also, secure compliance with WhatsApp’s policies to ensure there are no roadblocks or delays in setting up your APIs. Begin using your new tool to mobilize marketing campaigns over WhatsApp.",
                    ]}
                />

                <h2 className="text-black text-4xl font-semibold pb-5">Step Up Your WhatsApp Business Operations with Gallabox</h2>

                <ContentParagraph
                    content={
                        <p className="text-lg pt-2">
                            WhatsApp messaging solutions are an important marketing and lead-generation method for businesses of all sizes. This is because companies that used
                            messaging solutions experienced a
                            <a href="https://business.whatsapp.com/blog/use-whatsapp-business-goals" className="underline px-1">
                                58%
                            </a>
                            increase in leads.
                        </p>
                    }
                />

                <ContentParagraph
                    content={
                        <p className="text-lg pt-2">
                            If you want to power your lead pipeline with automation, AI, and personalization, consider implementing an all-rounder, impactful solution like
                            <a href="https://gallabox.com" className="underline px-1">
                                Gallabox
                            </a>
                        </p>
                    }
                />
                <ContentParagraph
                    content={[
                        "Gallabox’s comprehensive features empower you to transform not just lead capturing but also nurturing and conversion workflows with reliable, AI-based automations. Gallabox provides an AI-enabled chatbot with guided conversations and customizable flows to propel your interactions into conversions.",
                    ]}
                />

                <ContentParagraph
                    content={
                        <p className="text-lg pt-2 pb-20">
                            <a href="https://app.gallabox.com/upgrade" className="underline px-1">
                                Start a 7-day free trial
                            </a>
                            to leverage more features of our WhatsApp Business solution.
                        </p>
                    }
                />
            </div>

            <AlternativesCTASection />

            <FAQSection faqData={defaultFAQData} />

            <section className="bg-gray-100 md:py-20 py-10">
                <div
                    className="text-gray-900 max-w-6xl mx-auto mb-10 mt-5 md:ml-32 text-center md:text-6xl text-4xl font-semibold  md:text-left"
                    style={{ fontFamily: "Degular Display" }}
                >
                    Related Alternatives
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto gap-y-10">
                    {alternativesCardsDetails
                        .filter((data) => data.id !== "aisensy")
                        .map((data, idx) => (
                            <AlternativesCards key={idx} {...data} />
                        ))}
                </div>
            </section>

            <AwardsSection />

            <CTASection title="Turn connections into conversions" description="Take Gallabox for a 7-day ride" />
        </NewLayout>
    );
};

export default Aisensy;
